import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Button,
  TextField,
  Link,
  Typography,
  IconButton,
  InputAdornment,
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { versionSystem } from "../../../package.json";
import { nomeEmpresa } from "../../../package.json";
import { i18n } from "../../translate/i18n";
import { openApi } from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import logo from "../../assets/logo.png";

const B = "#059669";
const BD = "#047857";
const BL = "#10B981";
const SIDEBAR = "#0F172A";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100vw",
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f8fafb",
    padding: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
      alignItems: "flex-start",
      paddingTop: theme.spacing(4),
    },
  },
  card: {
    width: "100%",
    maxWidth: 420,
    backgroundColor: "#fff",
    borderRadius: 16,
    boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
    padding: theme.spacing(4, 4, 3),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(3, 2.5, 2.5),
      borderRadius: 12,
    },
  },
  logo: {
    maxWidth: 200,
    width: "100%",
    height: "auto",
    marginBottom: theme.spacing(2),
  },
  formTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: SIDEBAR,
    marginBottom: theme.spacing(0.5),
    alignSelf: "flex-start",
  },
  formSubtitle: {
    fontSize: 14,
    color: "rgba(0,0,0,0.5)",
    marginBottom: theme.spacing(2.5),
    alignSelf: "flex-start",
  },
  form: {
    width: "100%",
  },
  input: {
    marginBottom: theme.spacing(1),
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: "#fff",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: B,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: B,
    },
  },
  submit: {
    margin: theme.spacing(2, 0, 1.5),
    borderRadius: 10,
    fontWeight: 700,
    padding: "12px 0",
    fontSize: 15,
    background: `linear-gradient(135deg, ${B} 0%, ${BD} 100%)`,
    color: "#fff",
    boxShadow: "0 4px 14px rgba(5,150,105,0.3)",
    textTransform: "none",
    "&:hover": {
      background: `linear-gradient(135deg, ${BL} 0%, ${B} 100%)`,
      boxShadow: "0 6px 20px rgba(5,150,105,0.35)",
    },
  },
  linkRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1.5),
    color: "rgba(0,0,0,0.6)",
    fontSize: 13,
    fontWeight: 500,
  },
  linkText: {
    color: B,
    fontWeight: 600,
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  },
  copyright: {
    marginTop: theme.spacing(2),
    fontSize: 12,
    color: "rgba(0,0,0,0.35)",
    textAlign: "center",
  },
  stepIndicator: {
    display: "flex",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    alignSelf: "flex-start",
  },
  stepDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "rgba(0,0,0,0.12)",
  },
  stepDotActive: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: B,
  },
}));

const ForgetPassWord = () => {
  const classes = useStyles();
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSendEmail = async (values, actions) => {
    try {
      await openApi.post("/auth/forgot-password", { email: values.email });
      toast.success("Email enviado com sucesso!");
      setEmail(values.email);
      setStep(1);
    } catch (err) {
      toastError(err);
    }
    actions.setSubmitting(false);
  };

  const handleResetPassword = async (values, actions) => {
    try {
      await openApi.post("/auth/reset-password", {
        email,
        token: values.token,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      });
      toast.success("Senha alterada com sucesso!");
      window.location.href = "/login";
    } catch (err) {
      toastError(err);
    }
    actions.setSubmitting(false);
  };

  return (
    <div className={classes.root}>
      <div className={classes.card}>
        <img className={classes.logo} src={logo} alt={nomeEmpresa} />

        <Typography className={classes.formTitle}>
          {step === 0 ? "Recuperar senha" : "Nova senha"}
        </Typography>
        <Typography className={classes.formSubtitle}>
          {step === 0
            ? "Informe seu email para receber o código de verificação"
            : "Digite o código recebido e sua nova senha"}
        </Typography>

        <div className={classes.stepIndicator}>
          <div className={step === 0 ? classes.stepDotActive : classes.stepDot} />
          <div className={step === 1 ? classes.stepDotActive : classes.stepDot} />
        </div>

        {step === 0 ? (
          <Formik
            initialValues={{ email: "" }}
            validationSchema={Yup.object().shape({
              email: Yup.string().email("Email inválido").required("Obrigatório"),
            })}
            onSubmit={handleSendEmail}
          >
            {({ touched, errors, isSubmitting }) => (
              <Form className={classes.form}>
                <Field
                  as={TextField}
                  variant="outlined" margin="normal" fullWidth required
                  id="email" label="Email" name="email"
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  autoFocus className={classes.input}
                />
                <Button
                  type="submit" fullWidth variant="contained"
                  className={classes.submit} disabled={isSubmitting}
                >
                  Enviar código
                </Button>
              </Form>
            )}
          </Formik>
        ) : (
          <Formik
            initialValues={{ token: "", newPassword: "", confirmPassword: "" }}
            validationSchema={Yup.object().shape({
              token: Yup.string().required("Obrigatório"),
              newPassword: Yup.string().min(5, "Mínimo 5 caracteres").required("Obrigatório"),
              confirmPassword: Yup.string()
                .oneOf([Yup.ref("newPassword")], "Senhas não conferem")
                .required("Obrigatório"),
            })}
            onSubmit={handleResetPassword}
          >
            {({ touched, errors, isSubmitting }) => (
              <Form className={classes.form}>
                <Field
                  as={TextField}
                  variant="outlined" margin="normal" fullWidth required
                  id="token" label="Código de verificação" name="token"
                  error={touched.token && Boolean(errors.token)}
                  helperText={touched.token && errors.token}
                  autoFocus className={classes.input}
                />
                <Field
                  as={TextField}
                  variant="outlined" margin="normal" fullWidth required
                  name="newPassword" label="Nova senha"
                  type={showPassword ? "text" : "password"} id="newPassword"
                  error={touched.newPassword && Boolean(errors.newPassword)}
                  helperText={touched.newPassword && errors.newPassword}
                  className={classes.input}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword((v) => !v)} edge="end" size="small">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Field
                  as={TextField}
                  variant="outlined" margin="normal" fullWidth required
                  name="confirmPassword" label="Confirmar senha"
                  type={showConfirm ? "text" : "password"} id="confirmPassword"
                  error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                  helperText={touched.confirmPassword && errors.confirmPassword}
                  className={classes.input}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirm((v) => !v)} edge="end" size="small">
                          {showConfirm ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  type="submit" fullWidth variant="contained"
                  className={classes.submit} disabled={isSubmitting}
                >
                  Alterar senha
                </Button>
              </Form>
            )}
          </Formik>
        )}

        <div className={classes.linkRow}>
          <Link component={RouterLink} to="/login" className={classes.linkText}>
            Voltar ao login
          </Link>
        </div>

        <div className={classes.copyright}>
          {nomeEmpresa} &middot; v{versionSystem} &middot; {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
};

export default ForgetPassWord;
